import {
  Box3,
  BufferAttribute,
  BufferGeometry,
  DoubleSide,
  Group,
  Matrix3,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  PlaneGeometry,
  Scene,
  ShaderMaterial,
  Vector3,
} from 'three';
import { Subject } from 'rxjs';
import { printOrientationMatrices } from '../../utils/print-orientation.util';
import { SupportFacets, Vec3, supportFacets } from '../../utils/support-area.util';
import { supportVertexNormals } from '../../utils/support-overlay.util';
import { aimOverhangMaterial, createOverhangMaterial } from './overhang.material';
import { isClippingHelperMesh } from '../helpers';
import { ArrowHelper } from '../addons/load-helpers/arrowhelper';
import { OVERLAY_COLORS, arrowHead, overlayModelSize } from '../addons/visualization/overlay-style';

export interface PrintOrientationHost {
  readonly optimizedGroup: Group;
  readonly optimizedGroupParametric: Group;
  readonly infoGroup: Group;
  readonly scene: Scene;
  ucsTurn(): Matrix4;
  /** Whether the FEA field meshes are in the model's own frame rather than the UCS frame. */
  readonly resultsInModelFrame: boolean;
  /** Objects shown in the input orientation (input model, symmetry planes); hidden while in print view. */
  inputModelObjects(): Object3D[];
  /** Asks for a redraw after the scene changed. */
  requestRender(): void;
  /** Re-applies the view turn, which is off while the print view is shown. */
  refreshViewTurn(): void;
}

export interface PrintOrientationView {
  /** Row-major 3×3 that turns the build direction onto +Z. */
  rotationMatrix: readonly number[];
}

/** Where to paint: the surface needing support when built along `direction` (viewer frame). */
export interface OverhangView {
  direction: Vec3;
  critAngleDeg: number;
}

const OVERLAY_FLAG = 'printOrientationOverlay';

/**
 * The overhangs of one result mesh: a mesh of its own over the part's position
 * attribute and index (shared, so neither is copied nor uploaded twice) with
 * one attribute of its own, the smoothed normals the paint is tested on.
 */
interface OverhangOverlay {
  readonly mesh: Mesh;
  /** The part's geometry the overlay was built over. */
  readonly source: BufferGeometry;
  /** The facets `supportNormal` was last filled from, so unchanged facets do no work. */
  facets: SupportFacets | null;
}

/**
 * A triangle surface of the result. Wires are LineSegments2, which is a Mesh
 * whose own `position` holds a unit quad rather than the wire, so it is neither
 * measured nor painted; nor are the clipping machinery's copies of each part,
 * nor the overlays this manager adds.
 */
function isSurfaceMesh(object: Object3D): object is Mesh {
  return (
    object instanceof Mesh &&
    !object.userData[OVERLAY_FLAG] &&
    !(object.geometry as { isInstancedBufferGeometry?: boolean }).isInstancedBufferGeometry &&
    !isClippingHelperMesh(object.name)
  );
}
const PLATE_COLOR = 0x9e9e9e;
const PLATE_MARGIN = 0.1;
/** The plate sits this fraction of the part's height below the floor, and at least PLATE_MIN_GAP mm. */
const PLATE_GAP_FRACTION = 0.001;
const PLATE_MIN_GAP = 0.01;
/** Arrow length as a share of the result's bounding diagonal. */
const ARROW_LENGTH_FRACTION = 0.75;

/**
 * Shows an Additive result in its print orientation: the CAD result and FEA
 * field meshes turned about the part's centre and resting on their original
 * floor, with a build plate below. The input model and symmetry planes stay in
 * the input orientation, so they are hidden while the print view is shown and
 * restored afterwards.
 *
 * Independently of that, it paints the surface that needs support for any
 * build direction, in either orientation, and measures the result's facets for
 * the support-area numbers.
 */
export class PrintOrientationManager {
  private view: PrintOrientationView | null = null;
  private plate: Mesh | null = null;
  /** Objects this manager hid on show; only these are made visible again on reset. */
  private hiddenInputs: Object3D[] = [];

  private overhang: OverhangView | null = null;
  private overhangMaterial: ShaderMaterial | null = null;
  /** Result mesh → the overlay drawing its overhangs. */
  private overlays = new Map<Mesh, OverhangOverlay>();
  private arrow: ArrowHelper | null = null;
  /** Bounds of the result in its original orientation, for the arrow; keyed by meshes and group matrix. */
  private arrowBounds: { key: string; box: Box3 } | null = null;

  private facetCache = new WeakMap<Mesh, { key: string; facets: SupportFacets }>();
  private surfaceKey = '';
  /** Fires when the result meshes the support area is measured on have changed. */
  readonly surfaceChanged = new Subject<void>();

  constructor(private readonly host: PrintOrientationHost) {}

  get active(): boolean {
    return this.view !== null;
  }

  /**
   * Places the result groups for the current view; call whenever they are
   * rebuilt or the UCS changes. Outside the print view the model is shown as
   * imported: the CAD results arrive that way and the field meshes, in the
   * UCS frame, get the turn undone. Field meshes already in the model's own
   * frame (after an FEA run) are first turned into the UCS frame.
   */
  align(): void {
    const turn = this.host.ucsTurn();
    const fieldsToUcs = this.host.resultsInModelFrame ? turn.clone() : new Matrix4();
    if (!this.view) {
      this.setMatrices(new Matrix4(), turn.clone().invert().multiply(fieldsToUcs));
    } else {
      const { results, fields, bounds } = printOrientationMatrices(
        this.view.rotationMatrix,
        turn,
        (m) => this.measure(m),
      );
      this.setMatrices(results, fields.clone().multiply(fieldsToUcs));
      this.placePlate(bounds);
    }
    this.syncOverhangs();
    this.noticeSurfaceChange();
  }

  show(view: PrintOrientationView): void {
    this.clearPlate();
    this.view = view;
    this.align();
    this.hideInputModel();
    this.host.refreshViewTurn();
    this.host.requestRender();
  }

  /** Back to the original orientation. The camera stays where the user left it. */
  reset(): void {
    this.view = null;
    this.clearPlate();
    this.align();
    this.restoreInputModel();
    this.host.refreshViewTurn();
    this.host.requestRender();
  }

  /**
   * Paints the surface that needs support for `view`, or stops painting it
   * (null). While the part is in its original orientation an arrow shows the
   * build direction. The paint uses the support-area rule's threshold on
   * normals smoothed from the same facets the numbers are summed from, so the
   * region follows the surface instead of single triangles; the numbers stay
   * per facet. Changing the direction or angle only re-aims the material.
   */
  setOverhangs(view: OverhangView | null): void {
    this.overhang = view;
    this.syncOverhangs();
    this.host.requestRender();
  }

  /**
   * Normals and areas of every result facet in the viewer frame (the one the
   * part is shown in before it is turned), cached per mesh.
   */
  supportFacets(): SupportFacets[] {
    this.host.optimizedGroup.updateMatrixWorld(true);
    const out: SupportFacets[] = [];
    for (const mesh of this.surfaceMeshes()) {
      const facets = this.facetsOf(mesh);
      if (facets) out.push(facets);
    }
    return out;
  }

  /**
   * The facets of one result mesh, cached while its geometry and its place in
   * the viewer frame stay the same. The numbers are summed from them and the
   * overlays' smoothed normals are averaged from them.
   */
  private facetsOf(mesh: Mesh): SupportFacets | null {
    const geometry = mesh.geometry as BufferGeometry;
    const position = geometry.getAttribute('position');
    if (!position) return null;
    const matrix = this.meshToViewer(mesh);
    const key = this.facetKey(mesh, matrix);
    const cached = this.facetCache.get(mesh);
    if (cached && cached.key === key) return cached.facets;
    const index = geometry.getIndex();
    const facets = supportFacets(position.array, index ? index.array : null, matrix);
    this.facetCache.set(mesh, { key, facets });
    return facets;
  }

  /**
   * Mesh → viewer frame: the local matrices from the mesh up to the result
   * group, then the UCS turn. Built from the local matrices rather than by
   * undoing the group's world matrix, so turning the group for the print view
   * leaves it bit for bit the same and the cached facets stay valid.
   */
  private meshToViewer(mesh: Mesh): Matrix4 {
    const group = this.host.optimizedGroup;
    const chain = new Matrix4();
    for (let object: Object3D | null = mesh; object && object !== group; object = object.parent) {
      chain.premultiply(object.matrix);
    }
    return this.host.ucsTurn().multiply(chain);
  }

  private facetKey(mesh: Mesh, matrix = this.meshToViewer(mesh)): string {
    return `${mesh.geometry.uuid}:${matrix.elements.join(',')}`;
  }

  private surfaceMeshes(): Mesh[] {
    const meshes: Mesh[] = [];
    this.host.optimizedGroup.traverse((object: Object3D) => {
      if (isSurfaceMesh(object)) meshes.push(object);
    });
    return meshes;
  }

  private noticeSurfaceChange(): void {
    this.host.optimizedGroup.updateMatrixWorld(true);
    const key = this.surfaceMeshes()
      .map((mesh) => this.facetKey(mesh))
      .join('|');
    if (key === this.surfaceKey) return;
    this.surfaceKey = key;
    this.surfaceChanged.next();
  }

  private hideInputModel(): void {
    for (const object of this.host.inputModelObjects()) {
      if (!object.visible) continue;
      object.visible = false;
      this.hiddenInputs.push(object);
    }
  }

  private restoreInputModel(): void {
    for (const object of this.hiddenInputs) object.visible = true;
    this.hiddenInputs = [];
  }

  private setMatrices(results: Matrix4, fields: Matrix4): void {
    for (const group of [this.host.optimizedGroup, this.host.optimizedGroupParametric]) {
      results.decompose(group.position, group.quaternion, group.scale);
      group.updateMatrix();
    }
    fields.decompose(this.host.infoGroup.position, this.host.infoGroup.quaternion, this.host.infoGroup.scale);
    this.host.infoGroup.updateMatrix();
  }

  /** Exact scene bounds of the CAD result if its group carried `groupMatrix`. */
  private measure(groupMatrix: Matrix4): Box3 {
    const group = this.host.optimizedGroup;
    group.updateMatrixWorld(true);
    const groupInverse = group.matrixWorld.clone().invert();
    const box = new Box3();
    const point = new Vector3();

    for (const mesh of this.surfaceMeshes()) {
      const position = (mesh.geometry as BufferGeometry).getAttribute('position');
      if (!position) continue;
      const toScene = groupMatrix.clone().multiply(groupInverse.clone().multiply(mesh.matrixWorld));
      for (let i = 0; i < position.count; i++) {
        box.expandByPoint(point.fromBufferAttribute(position, i).applyMatrix4(toScene));
      }
    }
    return box;
  }

  /** The viewer-frame direction `d` where the result group currently shows it. */
  private toWorldDirection(d: Vec3): Vector3 {
    const group = this.host.optimizedGroup;
    group.updateMatrixWorld(true);
    const viewerToWorld = group.matrixWorld.clone().multiply(this.host.ucsTurn().invert());
    return new Vector3(d[0], d[1], d[2]).applyMatrix3(new Matrix3().setFromMatrix4(viewerToWorld)).normalize();
  }

  private syncOverhangs(): void {
    const view = this.overhang;
    if (!view) {
      this.clearOverlays();
      this.clearArrow();
      return;
    }
    const material = (this.overhangMaterial ??= createOverhangMaterial());

    this.host.optimizedGroup.updateMatrixWorld(true);
    const meshes = new Set(this.surfaceMeshes());
    for (const [parent, overlay] of this.overlays) {
      if (meshes.has(parent) && overlay.source === parent.geometry && overlay.mesh.parent === parent) continue;
      this.disposeOverlay(overlay);
      this.overlays.delete(parent);
    }
    for (const parent of meshes) {
      const facets = this.facetsOf(parent);
      if (!facets) continue;
      let overlay = this.overlays.get(parent);
      if (!overlay) {
        overlay = this.createOverlay(parent, material);
        this.overlays.set(parent, overlay);
      }
      this.fillOverlay(overlay, facets, parent);
    }
    const worldDirection = this.toWorldDirection(view.direction);
    aimOverhangMaterial(material, worldDirection, view.critAngleDeg);
    this.placeArrow(worldDirection);
  }

  private createOverlay(parent: Mesh, material: ShaderMaterial): OverhangOverlay {
    const source = parent.geometry as BufferGeometry;
    const position = source.getAttribute('position');
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', position);
    geometry.setIndex(source.getIndex());
    // x, y, z, w per vertex (see supportVertexNormals), in the part's own frame.
    geometry.setAttribute('supportNormal', new BufferAttribute(new Int16Array(position.count * 4), 4, true));
    if (!source.boundingSphere) source.computeBoundingSphere();
    geometry.boundingSphere = source.boundingSphere?.clone() ?? null;

    const mesh = new Mesh(geometry, material);
    mesh.name = 'print-orientation-overhangs';
    mesh.userData[OVERLAY_FLAG] = true;
    mesh.raycast = () => undefined;
    // Drawn after the part, so the part cannot cover it where depths are equal.
    mesh.renderOrder = parent.renderOrder + 1;
    mesh.frustumCulled = parent.frustumCulled;
    parent.add(mesh);
    return { mesh, source, facets: null };
  }

  /**
   * Fills the overlay's smoothed normals from `facets` (viewer frame), turned
   * back into the part's own frame. Only needed when the facets change.
   */
  private fillOverlay(overlay: OverhangOverlay, facets: SupportFacets, parent: Mesh): void {
    if (overlay.facets === facets) return;
    overlay.facets = facets;
    const geometry = overlay.mesh.geometry as BufferGeometry;
    const attribute = geometry.getAttribute('supportNormal') as BufferAttribute;
    const out = attribute.array as Int16Array;
    const position = overlay.source.getAttribute('position');
    const index = overlay.source.getIndex();
    const smooth = supportVertexNormals(position.array, index ? index.array : null, facets);
    // Normals go viewer → part by the transpose of the part → viewer matrix.
    const m = this.meshToViewer(parent).elements;
    const count = Math.min(attribute.count, smooth.length / 4);
    for (let v = 0, i = 0; v < count; v++, i += 4) {
      const x = smooth[i], y = smooth[i + 1], z = smooth[i + 2];
      const lx = m[0] * x + m[1] * y + m[2] * z;
      const ly = m[4] * x + m[5] * y + m[6] * z;
      const lz = m[8] * x + m[9] * y + m[10] * z;
      const length = Math.sqrt(lx * lx + ly * ly + lz * lz);
      const scale = length > 0 ? 32767 / length : 0;
      out[i] = Math.round(lx * scale);
      out[i + 1] = Math.round(ly * scale);
      out[i + 2] = Math.round(lz * scale);
      out[i + 3] = smooth[i + 3] * 32767;
    }
    attribute.needsUpdate = true;
  }

  /**
   * Frees the overlay's own attribute and its GPU buffer. The position and
   * index are the part's own: they are detached first so disposing does not
   * free the part's buffers as well.
   */
  private disposeOverlay(overlay: OverhangOverlay): void {
    overlay.mesh.removeFromParent();
    const geometry = overlay.mesh.geometry as BufferGeometry;
    geometry.deleteAttribute('position');
    geometry.setIndex(null);
    geometry.dispose();
  }

  /** The build-direction arrow, while the part is shown in its original orientation. */
  private placeArrow(direction: Vector3): void {
    const bounds = this.overhang && !this.view ? this.originalBounds() : null;
    if (!this.overhang || !bounds || bounds.isEmpty()) {
      this.clearArrow();
      return;
    }
    const origin = bounds.getCenter(new Vector3());
    const size = bounds.getSize(new Vector3());
    const length = size.length() * ARROW_LENGTH_FRACTION;
    const head = arrowHead(length, overlayModelSize(size));
    if (!this.arrow) {
      this.arrow = new ArrowHelper('line', direction.clone(), origin, length * 2, OVERLAY_COLORS.buildDirection);
      this.arrow.name = 'print-orientation-build-direction';
      // Drawn on top through the part, so it reads from any side.
      this.arrow.traverse((object) => {
        object.layers.set(0);
        object.raycast = () => undefined;
      });
      this.host.scene.add(this.arrow);
    }
    this.arrow.position.copy(origin);
    this.arrow.setDirection(direction.clone());
    // ArrowHelper draws half its length from its origin.
    this.arrow.setLength(length * 2, head.length, head.radius);
  }

  private originalBounds(): Box3 {
    const matrix = this.host.optimizedGroup.matrix;
    const key = `${this.surfaceMeshes().map((mesh) => mesh.geometry.uuid).join(',')}|${matrix.elements.join(',')}`;
    if (this.arrowBounds?.key !== key) this.arrowBounds = { key, box: this.measure(matrix) };
    return this.arrowBounds.box;
  }

  private placePlate(bounds: Box3): void {
    if (bounds.isEmpty()) return;
    if (!this.plate) {
      this.plate = new Mesh(
        new PlaneGeometry(1, 1),
        new MeshBasicMaterial({ color: PLATE_COLOR, transparent: true, opacity: 0.25, side: DoubleSide, depthWrite: false }),
      );
      this.plate.name = 'print-orientation-plate';
      // After the overhang paint, so the plate tints it like the rest of the part seen from below.
      this.plate.renderOrder = 998;
      this.plate.raycast = () => undefined;
      this.host.scene.add(this.plate);
    }
    const size = bounds.getSize(new Vector3());
    const center = bounds.getCenter(new Vector3());
    this.plate.scale.set(Math.max(size.x, 1) * (1 + PLATE_MARGIN), Math.max(size.y, 1) * (1 + PLATE_MARGIN), 1);
    const gap = Math.max(size.z * PLATE_GAP_FRACTION, PLATE_MIN_GAP);
    this.plate.position.set(center.x, center.y, bounds.min.z - gap);
  }

  private clearOverlays(): void {
    for (const overlay of this.overlays.values()) this.disposeOverlay(overlay);
    this.overlays.clear();
    this.overhangMaterial?.dispose();
    this.overhangMaterial = null;
  }

  private clearArrow(): void {
    if (!this.arrow) return;
    this.arrow.removeFromParent();
    this.arrow.dispose();
    this.arrow = null;
  }

  private clearPlate(): void {
    if (!this.plate) return;
    this.plate.removeFromParent();
    this.plate.geometry.dispose();
    (this.plate.material as MeshBasicMaterial).dispose();
    this.plate = null;
  }
}
